import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import apiClient from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import type { ExpenseCategoryDefinition } from '@/types'
import { NumericKeypad } from './NumericKeypad'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { formatMoney } from '@/lib/currency'
import { newIdempotencyKey } from '@/lib/inventoryFlow/offlineQueue'
import { BT } from '@/lib/inventoryFlow/boughtStrings'
import type { MarketDeliverTo } from '@/lib/inventoryFlow/marketTypes'
import { addMarketLine, marketProblem, marketTotal, stockCashOutBody, type MarketDraftLine } from '@/lib/inventoryFlow/marketLogic'
import { businessTodayIso } from '@/lib/expiry'
import { useDayBoundaryHour } from '@/hooks/useDayBoundaryHour'
import {
  BoughtItemsEditor,
  ChoiceRow,
  type BillPhoto,
} from '@/components/store/purchasing/BoughtItemsEditor'
import {
  NOT_A_COST,
  STAFF_ADVANCE,
  cashMovementProblem,
  effectiveKind,
  kindLabel,
  movementReason,
  quickReasons,
  stockItemForReason,
  type CashMovementType,
  type ReasonPreset,
} from './cashMovementLogic'

const chipOn = 'border-primary bg-primary/10 text-foreground font-medium'
const chipOff = 'border-input bg-background text-foreground hover:bg-accent'
const chipBase = 'rounded-lg border px-3 py-2 text-left text-sm transition-colors'

interface CashMovementDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultType?: CashMovementType
}

/**
 * Cash in or out of the drawer that wasn't a customer sale (F-FE-N185).
 *
 * Two panes: the left says why, the right says how much, with the keypad
 * always on screen. Why is one tap (F-FE-N188): a reason this restaurant has
 * used before (it already knows its expense kind), any of the owner's expense
 * kinds, or a "not a cost" reason. Typing only adds a detail. The Save button
 * names the action and the amount, or what is still missing.
 */
export function CashMovementDialog({
  open,
  onOpenChange,
  defaultType = 'paid_out',
}: CashMovementDialogProps) {
  const qc = useQueryClient()
  const [type, setType] = useState<CashMovementType>(defaultType)
  const [amount, setAmount] = useState<string>('')
  // The reason chip picked, or null when only a kind (or nothing) is.
  const [chip, setChip] = useState<string | null>(null)
  const [customReason, setCustomReason] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  // null = not decided yet; '' = not an expense.
  const [expenseCategory, setExpenseCategory] = useState<string | null>(null)
  const [showNotes, setShowNotes] = useState(false)
  // F-CTRL-N75: a stock cash-out says what it bought, so the stock arrives
  // with the money — or it is saved as "stock not added" for later.
  const kitchenFlow = useKitchenFlowEnabled()
  const today = businessTodayIso(useDayBoundaryHour())
  const [itemsLater, setItemsLater] = useState(false)
  const [deliverTo, setDeliverTo] = useState<MarketDeliverTo>('store')
  const [lines, setLines] = useState<MarketDraftLine[]>([])
  const [photos, setPhotos] = useState<BillPhoto[]>([])
  const [key, setKey] = useState(newIdempotencyKey)

  const categoriesQuery = useQuery({
    queryKey: ['expense-category-definitions'],
    queryFn: () => apiClient.getExpenseCategoryDefinitions(),
    enabled: open,
  })
  const categories = useMemo(
    () => ((categoriesQuery.data?.data ?? []) as ExpenseCategoryDefinition[]).filter((c) => c.is_active),
    [categoriesQuery.data],
  )
  const reasonsQuery = useQuery({
    queryKey: ['day-ops', 'cash-reasons'],
    queryFn: () => apiClient.getCashMovementReasons(),
    staleTime: 60_000,
    enabled: open,
  })
  const learned = useMemo(() => reasonsQuery.data?.data ?? [], [reasonsQuery.data])
  // Same query key as the items editor, so the list is fetched once.
  const itemsQuery = useQuery({
    queryKey: ['inventory-flow', 'items'],
    queryFn: () => apiClient.getFlowItems(),
    staleTime: 60_000,
    enabled: open && kitchenFlow,
  })
  const flowItems = useMemo(() => itemsQuery.data?.data ?? [], [itemsQuery.data])

  // Split by accounting treatment so the two kinds of outgoing cash are not
  // presented as interchangeable (F-RPT-N22).
  const inventoryCategories = categories.filter((c) => c.is_inventory)
  const operatingCategories = categories.filter((c) => !c.is_inventory)

  const isOut = type === 'paid_out'
  // Asked only for money out, and only once the tenant's kinds have loaded; a
  // tenant with no kinds files nothing, as before.
  const needsKind = isOut && categoriesQuery.isSuccess && categories.length > 0
  const kind = needsKind ? effectiveKind(expenseCategory, categories) : expenseCategory
  const reason = movementReason(chip, customReason, needsKind && kind ? kindLabel(kind, categories) : '')
  // What the cashier said it was, for finding the stock item it means.
  const what = customReason.trim() || chip || ''
  const selectedIsInventory = !!kind && inventoryCategories.some((c) => c.slug === kind)
  const withItems = isOut && selectedIsInventory && kitchenFlow && !itemsLater
  const itemsTotal = marketTotal(lines)
  const itemsProblem = withItems ? marketProblem(lines, 'till', '') : null
  const total = withItems ? itemsTotal : Number(amount) || 0
  const problem = cashMovementProblem({ reason, needsKind, kind, withItems, itemsProblem, itemsTotal, amount })

  useEffect(() => {
    if (open) {
      setType(defaultType)
      setAmount('')
      setChip(null)
      setCustomReason('')
      setNotes('')
      setExpenseCategory(defaultType === 'paid_in' ? '' : null)
      setShowNotes(false)
      setItemsLater(false)
      setDeliverTo('store')
      setLines([])
      setPhotos([])
      setKey(newIdempotencyKey())
    }
  }, [open, defaultType])

  // The stock line a reason obviously means ("Milk"), so paying the milkman is
  // a quantity and a price rather than a search. Lines the cashier has priced
  // or changed are kept; untouched ones from an earlier reason are dropped.
  const prefillFor = (label: string) => {
    const item = stockItemForReason(flowItems, label)
    setLines((l) => {
      const kept = l.filter((x) => x.unit_cost.trim() !== '' || x.qty !== 1)
      if (!item || kept.some((x) => x.stock_item_id === item.id)) return kept
      return addMarketLine(kept, item, 1, today)
    })
  }

  const chooseType = (next: CashMovementType) => {
    if (next === type) return
    setType(next)
    // The chips differ per direction, so a chip can't carry over; typed text can.
    setChip(null)
    setExpenseCategory(next === 'paid_in' ? '' : null)
    setItemsLater(false)
    setLines([])
  }

  // A reason chip: it brings its own kind ('' = not a cost).
  const tapChip = (p: ReasonPreset) => {
    setChip(p.label)
    setItemsLater(false)
    setExpenseCategory(isOut ? (p.kind ?? null) : '')
    if (isOut && p.kind && inventoryCategories.some((c) => c.slug === p.kind)) {
      prefillFor(p.label)
    } else {
      setLines([])
    }
  }

  // One of the owner's expense kinds, picked directly.
  const chooseKind = (slug: string) => {
    setChip(null)
    setExpenseCategory(slug)
    setItemsLater(false)
    if (inventoryCategories.some((c) => c.slug === slug)) prefillFor(customReason.trim())
    else setLines([])
  }

  const mut = useMutation({
    mutationFn: () =>
      apiClient.addDayCashMovement({
        movement_type: type,
        amount: total,
        reason,
        notes: notes.trim() || undefined,
        expense_category: isOut && kind ? kind : undefined,
        ...(withItems
          ? { purchase: stockCashOutBody({ deliverTo, supplier: '', notes: '', lines, photos }) }
          : {}),
      }, withItems ? key : undefined),
    onSuccess: () => {
      toastHelpers.success(`${isOut ? 'Taken out' : 'Put in'}: ${formatMoney(total)} for ${reason}`)
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      qc.invalidateQueries({ queryKey: ['expenses'] })
      qc.invalidateQueries({ queryKey: ['stock-cash-outs'] })
      if (withItems) {
        qc.invalidateQueries({ queryKey: ['inventory-flow'] })
        qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
        qc.invalidateQueries({ queryKey: ['stockItems'] })
      }
      onOpenChange(false)
    },
    onError: (err: Error) => toastHelpers.apiError('Save cash drawer change', err),
  })

  const quick = quickReasons(type, learned, needsKind ? categories : [])
  // When no single stock item matched the reason, start the editor's search
  // with it — but only if it finds something.
  const editorSearch =
    lines.length === 0 && what && flowItems.some((i) => i.name.toLowerCase().includes(what.toLowerCase()))
      ? what
      : ''

  const kindChip = (c: ExpenseCategoryDefinition, i: number, list: ExpenseCategoryDefinition[]) => (
    <button
      key={c.slug}
      type="button"
      aria-pressed={chip === null && kind === c.slug}
      onClick={() => chooseKind(c.slug)}
      className={cn(
        chipBase,
        i === list.length - 1 && list.length % 2 === 1 && 'col-span-2',
        chip === null && kind === c.slug ? chipOn : chipOff,
      )}
    >
      {kindLabel(c.slug, categories)}
    </button>
  )

  return (
    <Dialog open={open} onOpenChange={(o) => !mut.isPending && onOpenChange(o)}>
      <DialogContent
        className={cn(
          'flex max-h-[90vh] w-full flex-col gap-0 overflow-hidden p-0',
          // A fixed height from md up, so the dialog doesn't jump as questions open.
          withItems ? 'max-w-5xl md:h-[90vh]' : 'max-w-3xl md:h-[min(90vh,620px)]',
        )}
      >
        <DialogHeader className="shrink-0 border-b px-5 py-3 pr-12">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="min-w-[14rem] flex-1 space-y-0.5 text-left">
              <DialogTitle>{isOut ? 'Take money out' : 'Put money in'}</DialogTitle>
              <DialogDescription>
                {isOut
                  ? "Cash leaving the drawer that isn't a customer refund."
                  : "Cash going into the drawer that isn't a customer sale."}
              </DialogDescription>
            </div>
            <div
              className="inline-flex rounded-lg border border-input bg-muted p-0.5"
              role="radiogroup"
              aria-label="Money in or out?"
            >
              {([
                ['paid_out', 'Take out', ArrowUpFromLine, 'text-rose-600'],
                ['paid_in', 'Put in', ArrowDownToLine, 'text-emerald-600'],
              ] as const).map(([value, label, Icon, tone]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={type === value}
                  onClick={() => chooseType(value)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    type === value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Icon className={cn('h-4 w-4', type === value && tone)} />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </DialogHeader>

        {/*
          One scroll on phones; two panes from md up, each scrolling on its own,
          so the keypad never sits below the fold. The keypad used to be the last
          question in one long column, and cashiers scrolled past nine expense
          tiles to reach it.
        */}
        <div
          className={cn(
            'grid min-h-0 flex-1 grid-cols-1 overflow-y-auto md:overflow-hidden',
            withItems
              ? 'md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]'
              : 'md:grid-cols-[minmax(0,1fr)_300px]',
          )}
        >
          <div className="min-w-0 px-5 py-4 md:overflow-y-auto">
            <Label className="mb-2 block text-sm font-medium">What for?</Label>
            {quick.reasons.length > 0 && (
              <>
                {quick.learned && needsKind && (
                  <p className="mb-1.5 text-xs font-medium text-muted-foreground">Used often</p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {quick.reasons.map((p, i) => {
                    const selected = chip === p.label
                    const spans = i === quick.reasons.length - 1 && quick.reasons.length % 2 === 1
                    return (
                      <button
                        key={p.label}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => tapChip(p)}
                        className={cn(chipBase, spans && 'col-span-2', selected ? chipOn : chipOff)}
                      >
                        {p.label}
                        {needsKind && p.kind !== undefined && (
                          <span className="block text-xs font-normal text-muted-foreground">
                            {kindLabel(p.kind, categories)}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              </>
            )}

            {/*
              Every expense kind is one tap (F-FE-N188). It used to open only
              after a reason was picked or typed, behind two follow-up
              questions, so cashiers tapped a wrong chip just to reach it.
              Stock stays a group of its own (F-RPT-N22): it is an asset whose
              cost lands in the P&L as COGS when it sells, so it must not be
              presented as one more running cost.
            */}
            {needsKind &&
              [
                { title: 'Stock', list: inventoryCategories },
                { title: 'Bills & running costs', list: operatingCategories },
              ].map((group) =>
                group.list.length > 0 ? (
                  <div key={group.title} className="mt-3">
                    <p className="mb-1.5 text-xs font-medium text-muted-foreground">{group.title}</p>
                    <div className="grid grid-cols-2 gap-2">{group.list.map(kindChip)}</div>
                  </div>
                ) : null,
              )}
            {needsKind && (
              <div className="mt-3">
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">Staff</p>
                <button
                  type="button"
                  aria-pressed={chip === STAFF_ADVANCE.label}
                  onClick={() => tapChip(STAFF_ADVANCE)}
                  className={cn(chipBase, 'w-full', chip === STAFF_ADVANCE.label ? chipOn : chipOff)}
                >
                  {STAFF_ADVANCE.label}
                  <span className="block text-xs font-normal text-muted-foreground">
                    {chip === STAFF_ADVANCE.label && kind === '' ? kindLabel('', categories) : kindLabel(STAFF_ADVANCE.kind!, categories)}
                  </span>
                </button>
                {chip === STAFF_ADVANCE.label && (
                  <button
                    type="button"
                    onClick={() => setExpenseCategory(kind === '' ? STAFF_ADVANCE.kind! : '')}
                    className="mt-1.5 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                  >
                    {kind === ''
                      ? `Count it as a wage cost (${kindLabel(STAFF_ADVANCE.kind!, categories)})`
                      : 'Already counted in a salary you record separately? Not an expense'}
                  </button>
                )}
              </div>
            )}
            {needsKind && (
              <div className="mt-3">
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">Not a cost</p>
                <div className="grid grid-cols-2 gap-2">
                  {NOT_A_COST.map((label, i) => (
                    <button
                      key={label}
                      type="button"
                      aria-pressed={chip === label}
                      onClick={() => tapChip({ label, kind: '' })}
                      className={cn(
                        chipBase,
                        i === NOT_A_COST.length - 1 && NOT_A_COST.length % 2 === 1 && 'col-span-2',
                        chip === label ? chipOn : chipOff,
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Input
              id="cash-movement-reason"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder={needsKind ? 'Add a detail (optional): who or what…' : 'Or type your own reason…'}
              aria-label={needsKind ? 'Add a detail' : 'Or type your own reason'}
              maxLength={120}
              className="mt-3 h-10 text-sm"
            />
            {selectedIsInventory && (
              <p className="mt-2 text-xs text-muted-foreground">
                Stock is counted under cost of goods, so this will not also show as a running cost.
              </p>
            )}

            <div className="mt-4">
              {showNotes ? (
                <div className="space-y-2">
                  <Label htmlFor="cash-movement-notes" className="text-sm font-medium">
                    Note (optional)
                  </Label>
                  <Textarea
                    id="cash-movement-notes"
                    rows={2}
                    autoFocus
                    placeholder="Receipt number, who you paid, anything to remember…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNotes(true)}
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                  <Plus className="h-3.5 w-3.5" /> Add a note (optional)
                </button>
              )}
            </div>
          </div>

          <div className="min-w-0 space-y-3 border-t bg-muted/30 px-5 py-4 md:overflow-y-auto md:border-l md:border-t-0">
            {withItems ? (
              <>
                <div>
                  <p className="text-sm text-muted-foreground">{BT.amountFromItems}</p>
                  <p className="text-3xl font-semibold tabular-nums">{formatMoney(itemsTotal)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">{BT.whatBought}</p>
                  <p className="text-sm text-muted-foreground">{BT.whatBoughtHelp}</p>
                </div>
                <BoughtItemsEditor
                  key={chip ?? 'typed'}
                  lines={lines}
                  onLines={setLines}
                  photos={photos}
                  onPhotos={setPhotos}
                  onError={(m) => toastHelpers.error(m)}
                  initialSearch={editorSearch}
                />
                <ChoiceRow
                  label={BT.deliverTo}
                  value={deliverTo}
                  options={(['store', 'kitchen'] as const).map((p) => [p, BT.places[p]] as const)}
                  onChange={setDeliverTo}
                />
                {/* Not while a pre-added line is still untouched: Save already says what's missing. */}
                {itemsProblem && lines.some((l) => l.unit_cost.trim() !== '') && (
                  <p className="text-sm text-destructive">{BT.problems[itemsProblem]}</p>
                )}
                <button
                  type="button"
                  onClick={() => setItemsLater(true)}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  {BT.addLater}
                </button>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="cash-movement-amount" className="text-sm font-medium">
                    How much?
                  </Label>
                  <Input
                    id="cash-movement-amount"
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                    className="h-12 text-2xl font-semibold tabular-nums"
                  />
                </div>
                <NumericKeypad value={amount} onChange={setAmount} compact />
                {isOut && selectedIsInventory && kitchenFlow && itemsLater && (
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>{BT.addLaterHelp}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setItemsLater(false)
                        prefillFor(what)
                      }}
                      className="font-medium text-primary hover:underline"
                    >
                      {BT.addNow}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <DialogFooter className="shrink-0 flex-row gap-2 border-t px-5 py-3">
          <Button
            variant="outline"
            size="lg"
            className="h-12 w-24 sm:w-36"
            onClick={() => onOpenChange(false)}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            className={cn(
              'h-12 flex-1 text-white',
              isOut ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700',
            )}
            onClick={() => mut.mutate()}
            disabled={!!problem || mut.isPending}
          >
            {mut.isPending ? 'Saving…' : problem ?? `${isOut ? 'Take out' : 'Put in'} ${formatMoney(total)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
